import { ProForm } from '@ant-design/pro-components'
import { Checkbox } from 'antd'
import React, { useMemo } from 'react'
import { EditableLabel } from '../components/EditableLabel'
import { EditableShell } from '../components/EditableShell'
import { RenderErrors } from '../components/RenderErrors'
import { useFieldContext } from '../createAppFormHook'
import { useEditableFieldMode } from '../hooks/useEditableFieldMode'

type OptionValue = string | number | boolean
type CheckboxGroupValue = OptionValue[] | null | undefined

type CheckboxOption = {
  label: React.ReactNode
  value: OptionValue
  disabled?: boolean
}

export type CheckboxGroupFieldProps = {
  label?: React.ReactNode
  className?: string
  options?: CheckboxOption[]
  items?: CheckboxOption[]
  disabled?: boolean
  value?: OptionValue[]
  onChange?: (next: OptionValue[]) => void
}

const optionKey = (value: OptionValue) => {
  return `${typeof value}:${String(value)}`
}

export function CheckboxGroupField(props: CheckboxGroupFieldProps) {
  const fieldCtx = useFieldContext<CheckboxGroupValue>()
  const { className, options, items, label, disabled, value: controlledValue, onChange } = props

  const editable = useEditableFieldMode<CheckboxGroupValue>()

  const normalizedOptions = useMemo<CheckboxOption[]>(() => {
    return options ?? items ?? []
  }, [items, options])

  const value = useMemo<OptionValue[]>(() => {
    if (Array.isArray(controlledValue)) {
      return controlledValue
    }

    return Array.isArray(fieldCtx.state.value) ? fieldCtx.state.value : []
  }, [controlledValue, fieldCtx.state.value])
  const errors = fieldCtx.state.meta.errors

  const selectedOptions = useMemo(() => {
    const selected = new Set(value)
    return normalizedOptions.filter(opt => selected.has(opt.value))
  }, [normalizedOptions, value])

  return (
    <EditableShell
      mode={editable.mode}
      enterEdit={editable.enterEdit}
      exitRead={editable.exitRead}
      className={className}
    >
      <ProForm.Item
        label={
          <EditableLabel
            mode={editable.mode}
            enterEdit={editable.enterEdit}
            exitRead={editable.exitRead}
            onUndo={editable.undoAndExit}
            canUndo={editable.canUndo}
          >
            {label}
          </EditableLabel>
        }
        validateStatus={errors.length > 0 ? 'error' : undefined}
        help={<RenderErrors errors={errors} />}
      >
        {editable.mode === 'read' ? (
          <span>
            {selectedOptions.length > 0
              ? selectedOptions.map((opt, idx) => (
                  <React.Fragment key={optionKey(opt.value)}>
                    {idx > 0 ? ', ' : ''}
                    {opt.label}
                  </React.Fragment>
                ))
              : '-'}
          </span>
        ) : (
          <Checkbox.Group
            disabled={disabled}
            options={normalizedOptions}
            value={value}
            onChange={next => {
              const values = next
              onChange?.(values)
              if (!onChange) {
                fieldCtx.handleChange(values as CheckboxGroupValue)
              }
            }}
            onBlur={editable.onBlur}
            onKeyDown={editable.onKeyDown}
          />
        )}
      </ProForm.Item>
    </EditableShell>
  )
}
